/**
 * The authoring-preview ghost resolver (§3 row 14): whichever of tile-box / tile-stroke-or-hover /
 * wall-run / room-run is currently open, resolved into a fully-resolved, renderer-agnostic spec list
 * — the direction/color decisions all live in pure helpers
 * (`strokeDirection`/`strokeTarget`/`wallRunDirection`) — for `pixiCombatBoard.ts`, which only draws.
 * A tile paint previews as the tile's own swatch (`terrain-tile-art` 5.5): the spec carries the cell
 * to paint, and the board draws its swatch per cell. Lifted verbatim out of `<CombatStage>`'s preview effect: pure in, pure out, so the
 * effect is now the one `setAuthoringPreview` call.
 */
import type { DamageType, EdgeMap, SquareCell, TerrainCell, TerrainMap, TileTypeId } from "@lancer-vtt/domain";
import { squareKey } from "@lancer-vtt/space";
import { CELL } from "../combatCells";
import {
  type CombatBrush,
  type CoverGrade,
  type Vertex,
  type WallHeight,
  roomRectFromCells,
  OPEN_GROUND_CELL,
  storedCellAt,
  strokeDirection,
  strokeTarget,
  wallRunDirection,
  wallRunEndpoint,
} from "../combatAuthoring";
import type { AuthoringPreviewSpec } from "../pixiCombatBoard";
import { EDGE_WALL, PREVIEW_ADD_FILL, PREVIEW_ADD_STROKE, PREVIEW_OPACITY, REMOVE_FILL, REMOVE_STROKE, WALL_STROKE } from "./constants";

export function authoringPreviewSpecs({
  tileBoxPreview,
  tilePreview,
  hoverCell,
  hoverTarget,
  wallPreview,
  roomPreview,
  brush,
  terrain,
  tile,
  coverGrade,
  hazard,
  wallHeight,
  wallCoverGrade,
  cols,
  rows,
  edges,
}: {
  tileBoxPreview: { start: SquareCell; end: SquareCell; erase: boolean } | null;
  tilePreview: { cells: SquareCell[]; target: TerrainCell } | null;
  hoverCell: SquareCell | null;
  hoverTarget: TerrainCell | null;
  wallPreview: { from: Vertex; to: Vertex; erase: boolean } | null;
  roomPreview: { start: SquareCell; end: SquareCell; erase: boolean } | null;
  brush: CombatBrush | null;
  terrain: TerrainMap;
  /** The tile brush's picked catalogue tile. */
  tile: TileTypeId;
  coverGrade: CoverGrade;
  hazard: DamageType;
  wallHeight: WallHeight;
  wallCoverGrade: CoverGrade;
  cols: number;
  rows: number;
  edges: EdgeMap;
}): AuthoringPreviewSpec[] {
  const specs: AuthoringPreviewSpec[] = [];
  if (tileBoxPreview && brush) {
    const rect = roomRectFromCells(tileBoxPreview.start, tileBoxPreview.end, { cols, rows });
    if (rect) {
      const target = tileBoxPreview.erase ? OPEN_GROUND_CELL : strokeTarget(brush, storedCellAt(terrain, tileBoxPreview.start), { tile, coverGrade, hazard });
      const remove = strokeDirection(target) === "remove";
      specs.push({
        kind: "box",
        x: rect.x * CELL,
        y: rect.y * CELL,
        w: rect.w * CELL,
        h: rect.h * CELL,
        fill: remove ? REMOVE_FILL : PREVIEW_ADD_FILL,
        opacity: remove ? 1 : PREVIEW_OPACITY,
        stroke: remove ? REMOVE_STROKE : PREVIEW_ADD_STROKE,
        label: `${rect.w}×${rect.h}`,
        swatch: remove ? null : target,
      });
    }
  } else if (tilePreview) {
    const add = strokeDirection(tilePreview.target) === "add";
    specs.push({
      kind: "cells",
      cells: tilePreview.cells,
      add,
      swatch: add ? tilePreview.target : null,
      cols,
      rows,
    });
  } else if (hoverCell && hoverTarget) {
    const add = strokeDirection(hoverTarget) === "add";
    specs.push({
      kind: "cells",
      cells: [hoverCell],
      add,
      swatch: add ? hoverTarget : null,
      cols,
      rows,
    });
  }
  if (wallPreview) {
    const end = wallRunEndpoint(wallPreview.from, wallPreview.to);
    const direction = wallPreview.erase
      ? "remove"
      : wallRunDirection(wallPreview.from, wallPreview.to, { edges, height: wallHeight, coverGrade: wallCoverGrade });
    specs.push({
      kind: "line",
      x1: wallPreview.from.x * CELL,
      y1: wallPreview.from.y * CELL,
      x2: end.x * CELL,
      y2: end.y * CELL,
      stroke: direction === "remove" ? REMOVE_STROKE : EDGE_WALL,
    });
  }
  if (roomPreview) {
    const rect = roomRectFromCells(roomPreview.start, roomPreview.end, { cols, rows });
    if (rect) {
      specs.push({
        kind: "box",
        x: rect.x * CELL,
        y: rect.y * CELL,
        w: rect.w * CELL,
        h: rect.h * CELL,
        fill: roomPreview.erase ? REMOVE_FILL : WALL_STROKE,
        opacity: roomPreview.erase ? 1 : 0.12,
        stroke: roomPreview.erase ? REMOVE_STROKE : WALL_STROKE,
        label: `${rect.w}×${rect.h}`,
        swatch: null,
      });
    }
  }
  return specs;
}
